import axios from 'axios'
import { useStore, useCheckout } from '../composables/store'
import braintree from 'braintree-web'
import Swal from 'sweetalert2'
import * as Sentry from '@sentry/vue'

const checkout = useCheckout()
const store = useStore()

// Shipping Functions

function formatShippingDescription(description, short) {
	const patterns = [
		{
			match: (desc) => desc.includes('USPS'),
			getName: (desc) => {
				if (desc.includes('First-Class Package International Service')) {
					return 'USPS First-Class Package International'
				} else if (desc.includes('Priority Mail International')) {
					return 'USPS Priority Mail International'
				}
				return 'USPS'
			},
			getType: (desc) => {
				if (desc.includes('First-Class Package International Service')) {
					return 'First-Class Mail'
				} else if (desc.includes('Priority Mail International')) {
					return 'Priority Mail'
				} else if (desc.includes('Priority Mail')) {
					return 'Priority Mail'
				}
				return 'Mail'
			},
		},
		{
			match: (desc) => desc.includes('UPS'),
			getName: (desc) => {
				if (desc.includes('Next Day Air')) {
					return 'UPS Next Day Air'
				} else if (desc.includes('2nd Day Air')) {
					return 'UPS 2nd Day Air'
				} else if (desc.includes('Worldwide Expedited')) {
					return 'UPS Worldwide Expedited'
				} else if (desc.includes('Worldwide Saver')) {
					return 'UPS Worldwide Saver'
				} else if (desc.includes('Ground')) {
					return 'UPS Ground'
				}
				return 'UPS'
			},
			getType: (desc) => {
				if (desc.includes('Next Day Air')) {
					return 'Next Day Air'
				} else if (desc.includes('2nd Day Air')) {
					return '2nd Day Air'
				} else if (desc.includes('Expedited')) {
					return 'Expedited'
				} else if (desc.includes('Saver')) {
					return 'Saver'
				} else if (desc.includes('Ground')) {
					return 'Ground'
				}
				return 'Shipping'
			},
		},
		{
			match: (desc) => desc.includes('Local Pickup'),
			getName: () => 'Local Pickup - Liberty Hill, TX',
			getType: () => 'Pickup',
		},
		{
			match: (desc) => desc.includes('Economy'),
			getName: () => 'Economy',
			getType: () => 'Economy',
		},
	]

	const matchedPattern = patterns.find((pattern) => pattern.match(description))

	if (matchedPattern) {
		const name = matchedPattern.getName(description)
		const type = matchedPattern.getType(description)

		// Extract estimated date
		const estimatedDate = description.includes('Estimated:') ? description.split('Estimated:')[1].trim().replace(')', '') : 'No Date'

		// Include type in the name only if they are different and the name doesn't already contain the type
		const nameWithDescription = name !== type && !name.includes(type) ? `${name} (${type})` : name

		if (short) {
			return name
		}

		//return `${nameWithDescription} | Estimated: ${estimatedDate}`
		return estimatedDate === 'No Date' ? `${nameWithDescription}` : `${nameWithDescription} | Estimated: ${estimatedDate}`
	}

	// Fallback for unknown formats
	return description
}

async function getShippingEstimates() {
	// Creates consignment using selected shipping address
	// Returns shipping options and consignment ID
	// Sets default shipping method on the checkout store and big commerce checkout
	checkout.shipping.fetching = true

	checkout.updateShippingOptions([])
	checkout.selectShipping(null)

	console.log('getShippingEstimates running....')
	if (!store.cart?.bcId) {
		console.log('No cart ID found, returning.')
		return
	}
	console.log('BC cart ID: ', store.cart?.bcId)

	let consignment = {
		bcCartId: store.cart?.bcId,
		address: {
			...(checkout.shipping.shippingAddress?.firstName && { first_name: checkout.shipping.shippingAddress.firstName }),
			...(checkout.shipping.shippingAddress?.lastName && { last_name: checkout.shipping.shippingAddress.lastName }),
			...(checkout.shipping.shippingAddress?.company && { company: checkout.shipping.shippingAddress.company }),
			...(checkout.shipping.shippingAddress?.address1 && { address1: checkout.shipping.shippingAddress.address1 }),
			...(checkout.shipping.shippingAddress?.address2 && { address2: checkout.shipping.shippingAddress.address2 }),
			...(checkout.shipping.shippingAddress?.phone && { phone: checkout.shipping.shippingAddress.phone }),
			...(checkout.shipping.shippingAddress?.city && { city: checkout.shipping.shippingAddress.city }),
			...(checkout.shipping.shippingAddress?.stateOrProvince && { state_or_province: checkout.shipping.shippingAddress.stateOrProvince }),
			...(checkout.shipping.shippingAddress?.countryCode && { country_code: checkout.shipping.shippingAddress.countryCode }),
			...(checkout.shipping.shippingAddress?.postalCode && { postal_code: checkout.shipping.shippingAddress.postalCode }),
		},
	}

	console.log('consignment: ', consignment)

	let response = await axios.post(`${store.api}/controllers/bigCommerce/shipping`, consignment)
	if (response?.data?.success) {
		// Save shipping options back to the checkout store
		checkout.setShippingOptions(response.data.shippingOptions)
		checkout.updateConsignmentId(response.data.consignmentId)
		// checkout.setDefaultShippingMethod()

		console.log('Shipping address: ', checkout.shipping.shippingAddress)
		// await selectShippingMethod()

		checkout.shipping.fetching = false
		return true
	} else {
		checkout.shipping.fetching = false
		if (response?.data?.errorText === 'Not Found') {
			console.log('Big Commerce cart no longer exists: creating new cart from local store')
			return Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Oh no! 🫤 Your cart no longer exists in the database. Please remove all items and re-populate the cart to continue with checkout.',
				confirmButtonText: 'OK',
			})
		} else {
			return Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'There was an error getting shipping estimates. Please verify your address info is accurate and try again.',
				confirmButtonText: 'OK',
			})
		}
	}
}

async function getShippingEstimatesNew(selectedAddress) {
	console.log('getShippingEstimatesNew running....')
	if (!store.cart?.bcId) {
		console.log('No cart ID found, returning.')
		return
	}
	console.log('BC cart ID: ', store.cart?.bcId)

	let consignment = {
		bcCartId: store.cart?.bcId,
		address: {
			...(selectedAddress?.firstName && { first_name: selectedAddress.firstName }),
			...(selectedAddress?.lastName && { last_name: selectedAddress.lastName }),
			...(selectedAddress?.company && { company: selectedAddress.company }),
			...(selectedAddress?.address1 && { address1: selectedAddress.address1 }),
			...(selectedAddress?.address2 && { address2: selectedAddress.address2 }),
			...(selectedAddress?.phone && { phone: selectedAddress.phone }),
			...(selectedAddress?.city && { city: selectedAddress.city }),
			...(selectedAddress?.stateOrProvince && { state_or_province: selectedAddress.stateOrProvince }),
			...(selectedAddress?.countryCode && { country_code: selectedAddress.countryCode }),
			...(selectedAddress?.postalCode && { postal_code: selectedAddress.postalCode }),
		},
	}

	console.log('consignment: ', consignment)

	let response = await axios.post(`${store.api}/controllers/bigCommerce/shipping`, consignment)
	if (response?.data?.success) {
		return { success: true, data: response.data.shippingOptions, consignmentId: response.data.consignmentId }
	} else {
		if (response?.data?.errorText === 'Not Found') {
			console.log('Big Commerce cart no longer exists: creating new cart from local store')
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Oh no! 🫤 Your cart no longer exists in the database. Please remove all items and re-populate the cart to continue with checkout.',
				confirmButtonText: 'OK',
			})
		} else {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'There was an error getting shipping estimates. Please verify your address info is accurate and try again.',
				confirmButtonText: 'OK',
			})
		}
	}
}

async function updateShippingAddress(data = null) {
	let consignment
	if (!data) {
		consignment = {
			address: checkout.shipping.shippingAddress,
			bcCartId: store?.cart?.bcId,
			consignmentId: checkout.shipping.consignmentId,
		}
	} else {
		consignment = { ...data }
	}
	console.log('updateShippingAddress running data: ', data)
	const url = `${store.api}/controllers/bigCommerce/shipping`

	//return { success: false }

	const response = await axios.put(url, data)
	console.log('updateShippingAddress response: ', response?.data)

	return response?.data
}

async function selectShippingMethod() {
	let isDigitalOnly = !store?.cart?.cartItems?.length && !!store?.cart?.certificates?.length
	const data = {
		checkoutId: store?.cart?.bcId,
		consignmentId: checkout.shipping.consignmentId,
		selectedShippingId: checkout.shipping?.selectedShipping?.id,
		isDigitalOnly,
	}

	const response = await axios.post(`${store.api}/controllers/bigCommerce/checkout/shipping`, data)

	console.log('update consignment with selected shipping: ', response?.data)

	updateCheckoutTotals(response?.data?.data)
}

// Braintree Functions

async function initializeVenmo(data) {
	const { clientToken, amount } = data
	let venmoInstance

	try {
		console.log('Initializing braintree client instance...')
		const clientInstance = await braintree.client.create({
			authorization: clientToken,
		})

		console.log('Initializing braintree venmo instance...')
		venmoInstance = await braintree.venmo.create({
			client: clientInstance,
			allowDesktopWebLogin: true,
			allowNewBrowserTab: false,
			mobileWebFallBack: true,
			// allowDesktop: true,
			paymentMethodUsage: 'single_use',
			totalAmount: amount,
		})

		console.log('venmoInstance: ', venmoInstance)
		console.log('venmoInstance string: ', JSON.stringify(venmoInstance))

		if (!venmoInstance.isBrowserSupported()) {
			console.log('Venmo is not supported in this browser.')
			return { success: false, message: 'Venmo is not supported in this browser.' }
		}

		console.log('Tokenizing Venmo payment...')
		const payload = await venmoInstance.tokenize()
		console.log('Venmo payment tokenized. Nonce:', payload.nonce)
		return { success: true, nonce: payload.nonce, clientInstance }
	} catch (error) {
		console.error('Error initializing Venmo checkout:', error)
		return { success: false, error }
	} finally {
		if (venmoInstance) {
			venmoInstance.teardown()
		}
	}
}

async function processBraintreePayment(data) {
	const { nonce, amount, bcOrderId, deviceData } = data
	try {
		const paymentData = {
			paymentMethodNonce: nonce,
			amount: amount,
			bcOrderId,
		}
		if (deviceData) paymentData.deviceData = deviceData

		const response = await axios.post(`${store.api}/controllers/braintree/processPayment`, paymentData)

		if (response.data.success) {
			console.log('Payment successful! Braintree Transaction ID:', response.data.data.id)
			return response.data
		} else {
			console.error('Payment failed:', response.data.error)
			Sentry.withScope((scope) => {
				scope.setTag('orderId', bcOrderId)
				scope.setTag('mangoCart', store?.cart?.id)
				scope.setTag('bcCart', store?.cart?.bcId)
				scope.setExtra('action', 'processBraintreePayment')
				scope.setExtra('response', response?.data || response)
				Sentry.captureMessage('Process Braintree Payment Failed')
			})
		}
	} catch (error) {
		console.error('Error processing payment:', error)
		Sentry.withScope((scope) => {
			scope.setTag('orderId', bcOrderId)
			scope.setTag('mangoCart', store?.cart?.id)
			scope.setTag('bcCart', store?.cart?.bcId)
			scope.setExtra('action', 'processBraintreePayment')
			Sentry.captureException(error)
		})
		throw error
	}
}

async function getClientToken() {
	// takes this.store.api from the frontend as an argument
	console.log('Fetching the client token for braintree...')
	try {
		const response = await axios.get(`${store.api}/controllers/braintree/clientToken`)
		if (!response.data.success) {
			return { success: false, message: response.data.message }
		}
		return { success: true, token: response.data.token }
	} catch (error) {
		console.error('Error getting client token:', error)
		return { success: false, message: 'Error getting client token' }
	}
}

async function getSandboxClientToken() {
	// takes this.store.api from the frontend as an argument
	console.log('Fetching the client token for braintree...')
	try {
		const response = await axios.get(`${store.api}/controllers/braintree/sandbox/clientToken`)
		if (!response.data.success) {
			return { success: false, message: response.data.message }
		}
		return { success: true, token: response.data.token }
	} catch (error) {
		console.error('Error getting client token:', error)
		return { success: false, message: 'Error getting client token' }
	}
}

async function fetchBCCheckout() {
	try {
		const response = await axios.get(`${store.api}/controllers/bigCommerce/checkout/${store.cart?.bcId}`)
		const checkoutSession = response?.data?.checkout

		return checkoutSession
	} catch (error) {
		console.error('Error fetching checkout session:', error)
		return null
	}
}

async function validateConsignment() {
	if (checkout.isDigitalOnly) return true
	// for validating the consignment right before placeOrder
	const checkoutSession = await fetchBCCheckout()
	const consignment = checkoutSession?.consignments?.[0]
	const selectedShipping = consignment?.selected_shipping_option
	return !!(
		checkoutSession?.id &&
		consignment?.id &&
		selectedShipping?.id &&
		consignment?.id === checkout.shipping.consignmentId &&
		selectedShipping?.id === checkout.shipping.selectedShipping?.id
	)
}

// Checkout Functions
async function fetchCheckoutTotals() {
	const checkoutSession = await fetchBCCheckout()
	updateCheckoutTotals(checkoutSession)
	return checkoutSession
}

function updateCheckoutTotals(checkoutSession) {
	if (!checkoutSession?.id) {
		console.log('checkout session missing id')
		return false
	}
	const taxes = checkoutSession?.taxes

	checkout.updateTaxes(taxes)

	let baseAmount = checkoutSession?.cart?.base_amount
	let cartAmountExTax = checkoutSession?.cart?.cart_amount_ex_tax
	let cartAmountIncTax = checkoutSession?.cart?.cart_amount_inc_tax
	let subTotalExTax = checkoutSession?.subtotal_ex_tax
	let subTotalIncTax = checkoutSession?.subtotal_inc_tax
	let taxTotal = checkoutSession?.tax_total
	let grandTotal = checkoutSession?.grand_total

	store.cart.subTotal = baseAmount
	store.cart.tax = taxTotal
	store.cart.total = grandTotal - checkoutSession?.shipping_cost_total_ex_tax

	return true
}

function parseExpiryDate(expiryDate) {
	const [month, year] = expiryDate.split(' / ').map(Number)
	const expiryYear = year < 100 ? 2000 + year : year // Assuming year is in the format "xx"
	return {
		expiryMonth: month,
		expiryYear: expiryYear,
	}
}

function validateCheckoutInfo(paymentMethod = '') {
	// Validate email (either logged in user or guest)
	if (!store?.user?.email && !checkout.guest?.email) {
		return { success: false, message: 'Please provide an email address to continue.' }
	}

	// Validate billing address
	if (!checkout.billing?.billingAddress?.postalCode) {
		return { success: false, message: 'Please provide a billing address to continue.' }
	}

	// Validate shipping address for physical products
	if (!checkout.shipping?.shippingAddress?.address1 && !!store?.cart?.cartItems?.length) {
		return { success: false, message: 'Please provide a shipping address to continue.' }
	}

	// Skip payment validation if not required
	if (checkout.paymentNotRequired) {
		return { success: true, message: 'Checkout information is valid' }
	}

	// Validate payment method (credit card)
	if (paymentMethod === 'cards') {
		if (checkout.payment.addingNewCard) {
			const newCard = checkout.payment.newCard
			const errors = []

			if (!newCard.number) errors.push('card number')
			if (!newCard.expiry) errors.push('expiration date')
			if (!newCard.name) errors.push('cardholder name')
			if (!newCard.cvv) errors.push('CVV')

			if (errors.length > 0) {
				return {
					success: false,
					message: `Please provide the following card information: ${errors.join(', ')}`,
				}
			}
		} else {
			const storedCard = checkout.payment.selectedCard
			if (storedCard?.type === 'stored_card') {
				if (!storedCard.last4) {
					return { success: false, message: 'Invalid stored card: Missing card number' }
				}
				if (!storedCard.token) {
					return { success: false, message: 'Invalid stored card: Missing card token' }
				}
				if (!checkout.payment.cardCVV) {
					return { success: false, message: 'Please enter the CVV for your stored card' }
				}
			} else {
				return { success: false, message: 'Please select a valid payment method' }
			}
		}
	}

	// Validate payment method (ACH)
	if (paymentMethod === 'ach') {
		if (checkout.payment.addingNewACH) {
			const newACH = checkout.payment.newACH
			const errors = []

			// Required fields for all ACH types
			if (!newACH.accountType) errors.push('account type')
			if (!newACH.accountNumber) errors.push('account number')
			if (!newACH.routingNumber) errors.push('routing number')
			if (!newACH.ownershipType) errors.push('ownership type')

			// Conditional validation based on ownershipType
			if (newACH.ownershipType === 'personal') {
				if (!newACH.firstName) errors.push('first name')
				if (!newACH.lastName) errors.push('last name')
			} else {
				if (!newACH.businessName) errors.push('business name')
			}

			if (errors.length > 0) {
				return {
					success: false,
					message: `Please provide the following ACH information: ${errors.join(', ')}`,
				}
			}
		} else {
			// Validate selected stored ACH
			const selectedACH = checkout.payment.selectedACH
			if (!selectedACH?.token) {
				return { success: false, message: 'Please select a valid ACH account' }
			}
			if (!selectedACH.firstName || !selectedACH.lastName) {
				return { success: false, message: 'Invalid ACH account: Missing account holder name' }
			}
		}
	}

	return { success: true, message: 'Checkout information is valid' }
}

async function handleCheckout(paymentMethod) {
	// validation moved to payment-table.vue
	// console.log(`Checkout helper 'handleCheckout' called`)

	// const validateCheckout = validateCheckoutInfo(paymentMethod)
	// if (!validateCheckout.success) {
	// 	return validateCheckout
	// }

	switch (paymentMethod) {
		case 'cards':
			return await processCardPayment()
			break
		case 'ach':
			return await processACHPayment()
			break
		case 'net30':
			return await processNet30Payment()
			break
		default:
			console.log('Unsupported payment method')
			console.log('paymentMethod: ', paymentMethod)
			Swal.fire('Internal Payment Error', 'An unexpected error occurred. Please try again in a new browser tab.', 'error')
			break
	}
}

async function processCardPayment() {
	let paymentMethod, cardDetails
	if (checkout.payment.addingNewCard) {
		paymentMethod = 'card'
		let parseDate = parseExpiryDate(checkout.payment.newCard.expiry)
		let expiryMonth = parseDate.expiryMonth
		let expiryYear = parseDate.expiryYear

		cardDetails = {
			type: 'card',
			number: checkout.payment.newCard.number,
			expiryMonth,
			expiryYear,
			name: checkout.payment.newCard.name,
			cvv: checkout.payment.newCard.cvv,
			saveNewCard: checkout.payment.saveNewCard,
		}
	} else {
		paymentMethod = 'stored_card'
		cardDetails = checkout.payment.selectedCard
	}

	let response = await placeOrder({ paymentMethod, cardDetails })

	console.log('Process Card Payment - placeOrder Response: ', response)

	if (response?.success) {
		return await handleCheckoutSuccess(response?.bcOrderId, undefined, undefined, undefined, true)
	} else {
		store.cart.paymentError = response?.errorData
		store.cart.paymentData = response?.data
		if (response?.bcOrderId) {
			checkout.bcOrderId = response.bcOrderId
		}

		return {
			success: false,
			error: response?.errorDetails?.description || 'Unexpected error processing checkout',
			message: response?.message || 'Error',
		}
	}
}

async function processACHPayment() {}

async function processNet30Payment() {
	let response = await placeOrder({ paymentMethod: 'net30' })

	console.log('Process Net 30 Payment - placeOrder Response: ', response)

	if (response?.success) {
		return await handleCheckoutSuccess(response?.bcOrderId, undefined, undefined, 'net30', false)
	} else {
		store.cart.paymentError = response?.errorData
		store.cart.paymentData = response?.data
		if (response?.bcOrderId) {
			checkout.bcOrderId = response.bcOrderId
		}

		return {
			success: false,
			error: response?.errorDetails?.description || 'Unexpected error processing checkout',
			message: response?.message || 'Error',
		}
	}
}

async function placeOrder({ paymentMethod, cardDetails }) {
	// Note, if the checkout is abandoned AFTER the order is created but BEFORE payment is complete, if no changes are made to the cart and
	// the user returns to the checkout, a new order will be created with the original cart id. Note that this is acceptable. For more info,
	// see https://chatgpt.com/share/6738f7de-d070-8000-8420-e00b454ec8f2

	// axios POST request to checkout/orders
	let order = {
		checkoutId: store.cart.bcId,
		email: store?.user?.email || checkout?.guest?.email || checkout.billing.billingAddress.email,
		shippingAddress: checkout.shipping.shippingAddress,
		billingAddress: checkout.billing.billingAddress,
		shippingMethod: checkout.shipping.selectedShipping,
		paymentMethod: paymentMethod,
		orderComments: checkout.orderDetails.comments,
		consignmentId: checkout.shipping?.consignmentId,
		existingBcOrderId: checkout.bcOrderId,
		isDigitalOnly: store.cart?.isDigitalOnly || false,
		staffNote: store.cart?.staffNote || '',
	}

	if (checkout.payment.useStoreCredit) order.useStoreCredit = true

	if (store?.cart?.certificate?.balance) order.certificate = store.cart.certificate

	if (paymentMethod === 'card' || paymentMethod === 'stored_card') {
		order.cardCVV = checkout.payment.cardCVV || null
		order.cardDetails = cardDetails
	}

	console.log('Order object from checkout helpers placeOrder: ', order)

	try {
		const response = await axios.post(`${store.api}/controllers/bigCommerce/checkout/orders`, order)
		console.log('Response from big commerce order: ', response.data)

		if (!response?.data?.success) {
			Sentry.withScope((scope) => {
				scope.setTag('mangoCart', store?.cart?.id || 'mangoCart not found')
				scope.setTag('guest', store?.user?.id ? 'false' : 'true')
				scope.setTag('bcCart', store?.cart?.bcId || 'bcCart not found')
				scope.setTag('User', store?.user?.email || checkout?.guest?.email || 'User not found')
				scope.setTag('success', response?.data?.success)
				scope.setExtra('shippingAddress', checkout.shipping?.shippingAddress || 'shippingAddress is empty')
				scope.setExtra('billingAddress', checkout.billing?.billingAddress || 'billingAddress is empty')
				scope.setExtra('selectedShipping', checkout.shipping?.selectedShipping || 'selectedShipping is empty')
				scope.setTag('action', 'placeOrder')
				scope.setExtra('response', response?.data || response)
				scope.setLevel('error')
				Sentry.captureMessage('Place Order Failed')
			})
		}

		return response.data
	} catch (error) {
		console.log('Error placing big commerce order: ', error)
		Sentry.withScope((scope) => {
			scope.setTag('mangoCart', store?.cart?.id || 'mangoCart not found')
			scope.setTag('guest', store?.user?.id ? 'false' : 'true')
			scope.setTag('bcCart', store?.cart?.bcId || 'bcCart not found')
			scope.setTag('User', store?.user?.email || checkout?.guest?.email || 'User not found')
			scope.setExtra('shippingAddress', checkout.shipping?.shippingAddress || 'shippingAddress is empty')
			scope.setExtra('billingAddress', checkout.billing?.billingAddress || 'billingAddress is empty')
			scope.setExtra('selectedShipping', checkout.shipping?.selectedShipping || 'selectedShipping is empty')
			scope.setTag('vueCart', store?.cart || 'vueCart not found')
			scope.setTag('action', 'placeOrder')
			Sentry.captureException(error)
		})
		return { success: false, message: error.message }
	}
}

async function handleCheckoutSuccess(bcOrderId, processPayment = { data: { status: '' } }, statusIds = [], paymentMethod = null, isCardPayment = false) {
	console.log('Handle Checkout Success')

	// Clear shipping info
	checkout.resetShippingAddress()

	const status = processPayment?.data?.status

	if (statusIds?.includes(status) && !isCardPayment) {
		try {
			await axios.put(`${store.api}/controllers/bigCommerce/checkout/orders/braintree`, {
				orderId: bcOrderId,
				apmTransaction: processPayment?.data?.id ? { id: processPayment.data.id } : undefined,
				status: { id: statusIds.includes('settlement_pending') ? 7 : 11 },
				paymentMethod: paymentMethod || undefined,
			})
		} catch (error) {
			console.error(`Error updating BC order ${bcOrderId} status`, error)
		}
	} else if (paymentMethod === 'net30') {
		await axios.put(`${store.api}/controllers/bigCommerce/checkout/orders/braintree`, {
			orderId: bcOrderId,
			status: { id: 7 },
			paymentMethod: 'NET30',
		})
	}

	if (!store?.user?.id) {
		// Guest checkout logic
		const orderResponse = await axios.get(`${store.api}/controllers/bigCommerce/sync/orders/${bcOrderId}`)
		const order = orderResponse.data?.data
		window.localStorage.setItem('guestOrder', JSON.stringify(order))
	}
	await clearCart()

	console.log('redirecting to order confirmation')
	store.checkoutSuccess = true
	return { success: true, redirectUrl: `/checkout/order-confirmation/${bcOrderId}` }
}

async function clearCart() {
	if (store?.user?.id) {
		console.log(`Clearing converted cart ${store.cart.id}...`)
		try {
			const deleteCart = await axios.post(`${store.api}/controllers/bigCommerce/checkout/carts/delete`, { id: store.cart.id })
			console.log(deleteCart?.data)
		} catch (error) {
			console.error('Error clearing converted cart', error)
		}
	}

	console.log('Resetting store cart...')
	store.cart = { cartItems: [], certificates: [] }
	store.originalCart = { cartItems: [], certificates: [] }
	window.localStorage.removeItem('cart')
	console.log('store cart reset')
}

export default {
	formatShippingDescription,
	getShippingEstimates,
	getShippingEstimatesNew,
	processBraintreePayment,
	getClientToken,
	getSandboxClientToken,
	handleCheckout,
	placeOrder,
	handleCheckoutSuccess,
	updateShippingAddress,
	initializeVenmo,
	fetchCheckoutTotals,
	updateCheckoutTotals,
	selectShippingMethod,
	validateCheckoutInfo,
	validateConsignment,
}
